import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  FormControl,
  LinearProgress,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";

import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import { FormikHelper } from "@/common/helpers/formik";
import { TypeHelper } from "@/common/helpers/type";
import { useTenantProfileV2 } from "@/common/hooks/entity/tenant/useTenantProfileV2";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  DamageDetectionDto,
  DamageDetectionSendToExternalDamageCostEvaluationDto,
  DamageDetectionSendToExternalDamageCostEvaluationResultDto,
  EntityType,
  TenantRequestDto,
} from "@/core/api/generated/v1";

import NoDataAlert from "../../AppAlerts/NoDataAlert";
import GeneralValidationError from "../../Error/GeneralValidationError";
import {
  GeneralBillToInfoInput,
  prepareGeneralBillToInfoInputDtoForApi,
} from "../General/BillTo/GeneralBillToInfoInput";
import TenantAutocomplete from "../Tenant/TenantAutocomplete";
import TenantRequestLink from "../TenantRequest/TenantRequestLink";
import VehicleDamagesAutocomplete from "../VehicleDamage/VehicleDamagesAutocomplete";

const stepNumber = {
  send: 0,
  done: 1,
};

const steps = [
  {
    label: "Send",
  },
  {
    label: "Done",
  },
];

interface OwnProps {
  damageDetection: DamageDetectionDto;
  onSave: (newValue: DamageDetectionSendToExternalDamageCostEvaluationResultDto) => void;
}

type Props = OwnProps & DialogProps;

export default function DamageDetectionSendToExternalDamageCostEvaluationModal({
  damageDetection,
  onSave,
  ...modalProps
}: Props) {
  const { enqueueSnackbar } = useAppSnackbar();

  const tenantProfile = useTenantProfileV2();

  const [activeStep, setActiveStep] = useState(stepNumber.send);
  const [createdTenantRequest, setCreatedTenantRequest] = useState<
    TenantRequestDto | null | undefined
  >(undefined);

  const vehicleId = damageDetection.vehicle?.id;

  const vehicleDamageIdsInitial = useMemo(
    () => damageDetection.items?.map((x) => x.damage?.id || "").filter(Boolean),
    [damageDetection],
  );
  const isAnyVehicleDamage = TypeHelper.isNotEmptyArray(vehicleDamageIdsInitial);

  const isIniting = !tenantProfile.profile;

  const closeModal = () => {
    modalProps.onClose && modalProps.onClose({}, "escapeKeyDown");
  };

  return (
    <Dialog
      maxWidth='lg'
      fullWidth
      keepMounted={false}
      {...modalProps}
      onClose={(event, reason) => {
        modalProps.onClose && modalProps.onClose(event, reason);
      }}
    >
      <AppModalTitle
        onClose={() => {
          closeModal();
        }}
      >
        Send damages to external cost evaluation
      </AppModalTitle>

      <DialogContent>
        {isIniting && <LinearProgress />}

        {!isIniting && !isAnyVehicleDamage && <NoDataAlert title='No damages to asses' />}

        {!isIniting && isAnyVehicleDamage && (
          <Stepper activeStep={activeStep} orientation='vertical'>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {activeStep === stepNumber.send && (
                    <Box>
                      <Formik<
                        BaseFormikValues & DamageDetectionSendToExternalDamageCostEvaluationDto
                      >
                        enableReinitialize
                        initialValues={{
                          assessorTenantId:
                            tenantProfile.profile?.settings?.tenantToTenant
                              ?.defaultAssessorTenantId || undefined, // auto-select default assessor company
                          vehicleDamageIds: vehicleDamageIdsInitial,
                          billTo: undefined,

                          submit: "",
                        }}
                        validationSchema={Yup.object().shape({
                          // vehicleId: Yup.string().required("Vehicle is required"),
                        })}
                        onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
                          try {
                            const response =
                              await apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdSendToExternalDamageCostEvaluationPost(
                                {
                                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                                  damageDetectionId: damageDetection.id || "",
                                  damageDetectionSendToExternalDamageCostEvaluationDto: {
                                    ...values,
                                    billTo: prepareGeneralBillToInfoInputDtoForApi(values.billTo),
                                  },
                                },
                              );
                            enqueueSnackbar("Request was successfully sent.", {
                              variant: "success",
                            });
                            setCreatedTenantRequest(response.data.tenantRequest);
                            setActiveStep((prevActiveStep) => prevActiveStep + 1);
                            onSave && onSave(response.data);

                            setStatus({ success: true });
                            setSubmitting(false);
                          } catch (err: any) {
                            ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                            setStatus({ success: false });
                            setSubmitting(false);
                          }
                        }}
                      >
                        {(formikProps) => {
                          const { errors, handleSubmit, isSubmitting, values, setFieldValue } =
                            formikProps;
                          const isDefaultAssessorCompanySelected =
                            !!values.assessorTenantId &&
                            values.assessorTenantId ==
                              tenantProfile.profile?.settings?.tenantToTenant
                                ?.defaultAssessorTenantId;

                          return (
                            <form noValidate onSubmit={handleSubmit}>
                              <Stack spacing={2}>
                                <Box>
                                  {isDefaultAssessorCompanySelected && (
                                    <Alert severity='info' sx={{ mb: 1 }}>
                                      Default assessor company is selected.
                                    </Alert>
                                  )}

                                  <FormControl margin='normal' fullWidth>
                                    <TenantAutocomplete
                                      entityId={values.assessorTenantId}
                                      searchFilters={{
                                        isInMyConnections: true,
                                      }}
                                      required
                                      label='Assessor company'
                                      textFieldProps={{
                                        error: Boolean(errors.assessorTenantId),
                                        helperText: ValidationHelper.getFormikErrorsAsString(
                                          errors.assessorTenantId,
                                        ),
                                      }}
                                      onChange={(newValue) => {
                                        setFieldValue("assessorTenantId", newValue?.id);
                                      }}
                                    />
                                  </FormControl>

                                  <FormControl fullWidth margin='normal'>
                                    <VehicleDamagesAutocomplete
                                      entities={undefined}
                                      entityIds={values.vehicleDamageIds}
                                      searchFilters={{
                                        vehicleId: vehicleId || "",
                                        // limit to initial damages
                                        ids: vehicleDamageIdsInitial,
                                      }}
                                      label='Damages'
                                      required
                                      disabled={!isAnyVehicleDamage}
                                      textFieldProps={{
                                        error: Boolean(errors.vehicleDamageIds),
                                        helperText: ValidationHelper.getFormikErrorsAsString(
                                          errors.vehicleDamageIds,
                                        ),
                                      }}
                                      onChange={(newValue) => {
                                        setFieldValue(
                                          "vehicleDamageIds",
                                          newValue?.map((x) => x.id || ""),
                                        );
                                      }}
                                    />
                                  </FormControl>

                                  {tenantProfile.profile?.settings?.tenantToTenant
                                    ?.isEnableBillTo && (
                                    <FormControl fullWidth margin='normal'>
                                      <GeneralBillToInfoInput
                                        formikProps={FormikHelper.getSubProps(
                                          formikProps,
                                          "billTo",
                                          (x) => x.billTo,
                                        )}
                                        inferFromEntities={[
                                          {
                                            entityType: EntityType.DamageDetection,
                                            entityId: damageDetection.id,
                                          },
                                          {
                                            entityType: EntityType.Vehicle,
                                            entityId: damageDetection.vehicle?.id,
                                          },
                                        ]}
                                      />
                                    </FormControl>
                                  )}
                                </Box>

                                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                                <Stack direction='row' gap={1}>
                                  <Button
                                    variant='outlined'
                                    color='text'
                                    onClick={() => {
                                      closeModal();
                                    }}
                                  >
                                    Cancel
                                  </Button>

                                  <Button
                                    type='submit'
                                    variant='contained'
                                    color='primary'
                                    loading={isSubmitting}
                                  >
                                    Send
                                  </Button>
                                </Stack>
                              </Stack>
                            </form>
                          );
                        }}
                      </Formik>
                    </Box>
                  )}

                  {activeStep === stepNumber.done && (
                    <Stack spacing={1}>
                      <Typography>
                        Company request <TenantRequestLink entity={createdTenantRequest} /> has been
                        created and sent.
                      </Typography>

                      <Box>
                        <Button
                          variant='outlined'
                          color='text'
                          onClick={() => {
                            closeModal();
                          }}
                        >
                          Close
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogContent>
    </Dialog>
  );
}
